// @givmo/design-system — DatePicker (Popover-anchored <Calendar>).
//
// SCOPE NOTE (03-3b → 03-3c hand-off): 03-3b shipped this DatePicker with a bespoke,
// self-contained input-trigger that toggled the calendar in a hand-rolled absolutely-
// positioned dropdown (no overlay primitive, because the Popover lived in 03-3c). 03-3c
// owns the shared <Popover>, so this component now renders its calendar inside that shared
// Popover — anchoring, flip/shift, dismiss-on-outside-click, Escape, focus restore, and the
// portal all come from Base UI. The public API + tests are unchanged.
//
// The trigger reuses the TextField visual language: a button styled with the two-state border
// (idle neutral-500 → open blue + focus-glow), all12 radius, white fill, a leading calendar glyph,
// and the formatted selected date (or a gray60 placeholder). Selecting a day closes the popup.
//
// a11y: the trigger is a real <button> rendered through <Popover.Trigger>, which wires
// `aria-haspopup="dialog"` + `aria-expanded`; the calendar grid carries react-day-picker's
// built-in grid/gridcell roles and keyboard nav. The popover closes on Escape and on outside
// click (Popover behavior). `testId` forwards to the trigger button.
import { useId, useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { Calendar } from './calendar';
import { ErrorMessage } from './error-message';
import { Popover } from './popover';

export interface DatePickerProps extends TestIdProps {
  /** Field label (Lato w600 s12, matching TextField). Omit to render no label. */
  label?: string;
  /**
   * Controlled selected date. `null` is controlled with no date (the placeholder shows): a controlled
   * caller passes `null` for an empty field, never `undefined`, which makes the picker uncontrolled,
   * so it would go on showing the last day picked inside it.
   */
  value?: Date | null;
  /** Uncontrolled initial selected date. */
  defaultValue?: Date;
  onValueChange?: (value: Date | undefined) => void;
  /** Placeholder shown when no date is selected (gray60). */
  placeholder?: string;
  /** Formatter for the selected date in the trigger. Defaults to a medium Intl date. */
  formatDate?: (date: Date) => string;
  disabled?: boolean;
  /** Error state — switches the trigger border to magenta and (with `errorMessage`) shows the row. */
  error?: boolean;
  /** Error text rendered in an inline <ErrorMessage> beneath the field. */
  errorMessage?: string;
  className?: string;
  /**
   * Names the field for assistive technology where no visible `label` does (a control in a row). The
   * trigger then reads as this name and its date (or placeholder), so the date is never hidden.
   */
  'aria-label'?: string;
  /**
   * Month and year lists in the calendar's caption, from `startMonth` through `endMonth`, for a date
   * that may sit years away (a last gift date): any month is two choices away. Off by default: the
   * caption names the month and the arrows step one month at a time.
   */
  monthYearRange?: { startMonth: Date; endMonth: Date };
  /**
   * Visual appearance (ADDITIVE, as Select's): `legacy` (default) is the Flutter-ported trigger, unchanged;
   * `saas` the admin consoles' field (h-36, neutral border, brand ring), so a date sits at the height of the
   * Field and Select beside it. The trigger, its popup, the calendar and the `testId` are the same in both.
   */
  appearance?: 'legacy' | 'saas';
}

const defaultFormat = (date: Date) =>
  new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(date);

export function DatePicker({
  label,
  value,
  defaultValue,
  onValueChange,
  placeholder = 'Select a date',
  formatDate = defaultFormat,
  disabled,
  error = false,
  errorMessage,
  className,
  testId,
  appearance = 'legacy',
  'aria-label': ariaLabel,
  monthYearRange,
}: DatePickerProps) {
  const reactId = useId();
  const errorId = `${reactId}-error`;
  const hasErrorMessage = error && !!errorMessage;
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState<Date | undefined>(defaultValue);
  const selected = isControlled ? (value ?? undefined) : internal;
  const [open, setOpen] = useState(false);
  const saas = appearance === 'saas';

  const handleSelect = (date: Date | undefined) => {
    if (!isControlled) setInternal(date);
    onValueChange?.(date);
    setOpen(false);
  };

  return (
    <div className={cn('flex flex-col items-stretch', className)}>
      {label &&
        (saas ? (
          <span className="mb-6 font-sans text-s13 font-w500 tracking-normal text-text-secondary">
            {label}
          </span>
        ) : (
          <span className="mb-8 font-sans text-s12 font-w600 tracking-givmo text-black">
            {label}
          </span>
        ))}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger
          disabled={disabled}
          aria-invalid={error || undefined}
          aria-describedby={hasErrorMessage ? errorId : undefined}
          aria-label={
            ariaLabel ? `${ariaLabel}, ${selected ? formatDate(selected) : placeholder}` : undefined
          }
          className={
            saas
              ? cn(
                  'flex h-36 w-full items-center gap-8 rounded-md border bg-surface px-12 text-left',
                  'font-sans text-s14 font-w500 tracking-normal',
                  'outline-none transition-[box-shadow,border-color]',
                  // The control boundary Select's SaaS trigger keeps: neutral-500 idle, darker on
                  // hover, brand-500 and its ring when open or focused, danger on error.
                  'border-neutral-500 hover:border-neutral-600',
                  open && 'border-brand-500 shadow-ring',
                  'focus-visible:border-brand-500 focus-visible:shadow-ring',
                  error &&
                    'border-danger-400 focus-visible:border-danger-400 focus-visible:shadow-ring-danger',
                  open && error && 'border-danger-400',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )
              : cn(
                  'flex w-full items-center gap-8 rounded-all12 border bg-white p-12 text-left',
                  'font-sans text-s16 font-w400 leading-h15 tracking-givmo',
                  'outline-none transition-shadow',
                  'border-neutral-500',
                  open && 'border-blue shadow-focus-glow',
                  'focus-visible:border-blue focus-visible:shadow-focus-glow',
                  error && 'border-magenta focus-visible:border-magenta',
                  open && error && 'border-magenta',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )
          }
          {...testIdProp(testId)}
        >
          <CalendarIcon
            size={saas ? 16 : 18}
            className={cn('shrink-0', saas ? 'text-icon-muted' : 'text-gray60')}
            aria-hidden
          />
          <span
            className={cn(
              'flex-1',
              saas
                ? cn('truncate', selected ? 'text-text' : 'text-text-placeholder')
                : selected
                  ? 'text-black'
                  : 'text-gray60',
            )}
          >
            {selected ? formatDate(selected) : placeholder}
          </span>
        </Popover.Trigger>
        <Popover.Content
          aria-label={label ?? ariaLabel ?? 'Choose date'}
          testId={testId ? `${testId}-popup` : undefined}
        >
          {/* Opens on the chosen day's month, not today's. */}
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            onSelect={handleSelect}
            autoFocus
            {...(monthYearRange
              ? {
                  captionLayout: 'dropdown' as const,
                  startMonth: monthYearRange.startMonth,
                  endMonth: monthYearRange.endMonth,
                }
              : {})}
            testId={testId ? `${testId}-calendar` : undefined}
          />
        </Popover.Content>
      </Popover.Root>
      {hasErrorMessage && (
        <ErrorMessage testId={testId ? `${testId}-error` : undefined}>
          <span id={errorId}>{errorMessage}</span>
        </ErrorMessage>
      )}
    </div>
  );
}
